import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Router } from "@angular/router";
import { faArrowLeft, faCloudUploadAlt, faDownload, faFolderOpen, faInfoCircle, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { TranslateService } from "@ngx-translate/core";
import { DropTargetOptions, FileItem, HttpClientUploadService, InputFileOptions } from "@wkoza/ngx-upload";

/** Taille maximale d'import par fichier (affichage indicatif dans la drop zone). */
const MAX_FILE_SIZE_LABEL = "2 Go";
import { UserService } from "app/core/services/admin/user/user.service";
import { LayoutService } from "app/core/services/global/layout/layout.service";
import { ToastService } from "app/core/services/global/toast/toast.service";
import { ACL } from "app/shared/models/acl";
import { MAESTRO_ROUTES } from "app/shared/routes";
import { map } from "rxjs/operators";
import { UploadService } from "../../../core/services/upload/upload.service";

@Component({
    selector: "app-uploader",
    templateUrl: "./uploader.component.html",
    styleUrls: ["./uploader.component.scss"],
})
export class UploaderComponent implements OnInit, OnDestroy {
    private _acceptList = [];

    options: DropTargetOptions;

    optionsInput: InputFileOptions;

    readonly faUpload = faCloudUploadAlt;
    readonly faFolderOpen = faFolderOpen;
    readonly faDownload = faDownload;
    readonly faArrowLeft = faArrowLeft;
    readonly faTrashAlt = faTrashAlt;
    readonly faInfoCircle = faInfoCircle;
    readonly routes = MAESTRO_ROUTES;
    readonly maxFileSizeLabel = MAX_FILE_SIZE_LABEL;

    /** Extensions affichables (JPG, PNG, …) construites depuis les types de médias autorisés. */
    acceptedFormats: string[] = [];

    acl: ACL;

    public disabled = true;

    claroAvailable = false;

    /** Mode pop-up : pilote le fil d'Ariane, la navigation et le pied d'actions (déclenché depuis la liste DAM). */
    @Input() modal = false;

    /** Émis après un import réussi (la liste DAM se recharge). */
    @Output() uploaded = new EventEmitter<void>();

    /** Demande de fermeture de la pop-up depuis le pied d'actions. */
    @Output() closeRequested = new EventEmitter<void>();

    constructor(
        private _layoutService: LayoutService,
        private _router: Router,
        private _uploadService: UploadService,
        public uploader: HttpClientUploadService,
        private _toaster: ToastService,
        private _translate: TranslateService,
        private _userService: UserService
    ) {}

    ngOnInit() {
        this.acl = this._userService.getUserAclFromToken();
        // Get available media types
        this._uploadService.getMediaTypes().subscribe((data) => {
            this._acceptList = [];

            data.map((v) => v.header).forEach((header) => {
                this._acceptList.push(...header.split(","));
            });

            // Labels lisibles (JPG, PNG, …) pour la drop zone, dédoublonnés en majuscules.
            this.acceptedFormats = [
                ...new Set(
                    data
                        .map((v) => (v.extension || "").trim().toUpperCase())
                        .filter((extension) => extension !== "")
                ),
            ];

            this.options = {
                color: "dropZoneColor",
                colorDrag: "dropZoneColorDrag",
                colorDrop: "dropZoneColorDrop",
                multiple: true,
                accept: this._acceptList,
            };

            this.optionsInput = {
                multiple: true,
                accept: this._acceptList,
            };
        });

        this._uploadService.claroIsAvailable().subscribe((data) => {
            this.claroAvailable = data.data;
        })

        this.disabled = true;
        this.uploader.onAddToQueue$.subscribe((item) => {
            this.disabled = false;
            this._initFileName(item);
        });
        this.uploader.onSuccess$.subscribe(() => {
            this.disabled = true;
            // En pop-up, on reste dans la liste DAM : pas de navigation vers la page d'import.
            if (!this.modal) {
                this._router.navigate(["/dam", "thumbnails"]);
            }
        });
        // Le fil d'Ariane n'est pertinent que sur la page d'import dédiée, pas dans la pop-up.
        if (!this.modal) {
            this._layoutService.breadcrumb.setPath({ name: "upload.title", routerLink: null }, 0);
        }
    }

    ngOnDestroy(): void {
        this.uploader.queue.length = 0;
        this.uploader.progressTotal = 0;
    }

    /**
     * Upload all media
     */
    upload(): void {
        this.disabled = true;
        let isPicto = false;
        let isClaro = false;

        if (this.acl.MAESTRO_DAM_PICTO_CREATE === 1) {
            isPicto = $("#picto:checked").length === 1;
        }

        if (this.claroAvailable === true) {
            isClaro = $("#claro:checked").length === 1;
        }

        // File.name étant en lecture seule, on reconstruit chaque fichier avec le nom saisi par l'utilisateur.
        const renamedFiles = this.uploader.queue.map((fi) => this._buildRenamedFile(fi));
        const finalNames = renamedFiles.map((file) => file.name);

        // L'upload est un POST groupé (pas le mécanisme par-fichier de ngx-upload) : on pilote le statut nous-mêmes.
        this.uploader.queue.forEach((f) => ((f as any).status = "uploading"));

        this._uploadService
            .uploadMedias(renamedFiles, isPicto, isClaro)
            .pipe(map((r: any) => r.data))
            .subscribe(
                (data) => {
                    data = data.map((d) => d.name);
                    this.disabled = false;
                    this.uploader.queue.forEach((f, index) => {
                        if (data.includes(finalNames[index])) {
                            (f as any).status = "success";
                            f.progress = 100;
                            setTimeout(() => {
                                if (this.uploader.queue.includes(f)) {
                                    f ? f.remove() : null;
                                }
                            }, 3000);
                        } else {
                            (f as any).status = "error";
                        }
                    });
                    this._toaster.show({ message: this._translate.instant("upload.done"), type: "success" });
                    // En pop-up, prévenir la liste DAM pour qu'elle recharge les médias importés.
                    if (this.modal) {
                        this.uploaded.emit();
                    }
                },
                () => {
                    this.disabled = false;
                    this.uploader.queue.forEach((f) => ((f as any).status = "error"));
                }
            );
    }

    /**
     * État courant d'un fichier de la file (pour le badge de la colonne « Statut »).
     * Le flux d'upload étant un POST groupé, l'état est porté par un champ `status` maison
     * (avec repli sur les drapeaux ngx-upload au cas où).
     */
    fileStatus(item: FileItem): "success" | "error" | "uploading" | "pending" {
        const status = (item as any).status;
        if (status) {
            return status;
        }
        if (item.isSuccess) {
            return "success";
        }
        if (item.isError) {
            return "error";
        }
        if (item.uploadInProgress) {
            return "uploading";
        }
        return "pending";
    }

    /**
     * Initialise le nom éditable (sans extension) et l'extension figée d'un fichier ajouté à la file.
     */
    private _initFileName(item: FileItem): void {
        const fileName = item.file.name;
        const dotIndex = fileName.lastIndexOf(".");
        const hasExtension = dotIndex > 0;
        (item as any).displayName = hasExtension ? fileName.slice(0, dotIndex) : fileName;
        (item as any).extension = hasExtension ? fileName.slice(dotIndex) : "";
        (item as any).status = "pending";
    }

    /**
     * Reconstruit un File avec le nom saisi par l'utilisateur (File.name est immuable).
     * Retombe sur le fichier d'origine si la saisie est vide.
     */
    private _buildRenamedFile(item: FileItem): File {
        const original = item.file;
        const base = ((item as any).displayName || "").trim();
        const extension = (item as any).extension || "";

        if (base === "") {
            return original;
        }

        return new File([original], base + extension, { type: original.type, lastModified: original.lastModified });
    }
}
